import { motion, useReducedMotion } from "framer-motion";

import {
	AiVisionsComputer,
	BusinessAdvisorLogo,
	CloudManagementLogo,
	ProjectManagementLogo,
	StrategicPlanningLogo,
	SystemDesign,
	TechnologyConsultantLogo
} from "../../../assets";
import { services } from "../../../content";
import type { Service, ServiceImageKey } from "../../../types/content";

/**
 * ============================================================================
 * Services Offered — direct consumer of the content layer.
 * ============================================================================
 *
 * Net-new in D-031. This is NOT the section D-017 retired: that one was
 * starter-template agency copy written in the "we" voice, carrying unevidenced
 * claims about a company that does not exist. This one renders the author's own
 * capabilities from `src/content/data/services.json`, and every word of it lives
 * in that file. To change what this section says, edit the JSON — not this file.
 *
 * No bridge (Constitution V.4): the section is new, so there is no legacy shape
 * to adapt. Tailwind for structure, mirroring Platforms; framer-motion for the
 * reveal, mirroring Experience_v2 down to its reduced-motion handling.
 *
 * Every colour is a token from `global/theme/tokens.css` (D-034) — this is one
 * of the three surfaces migrated in phase 1 of the theme work, so it is also
 * the proof that the token system flips. The dark values are byte-identical to
 * the hex literals they replaced, which is what makes "did anything change in
 * dark mode?" an answerable question rather than a judgement call.
 *
 * Distinct from Platforms on purpose, and the distinction is load-bearing:
 * Platforms records systems that were *built* and attaches an outcome to each,
 * while Services states capabilities *offered* and claims no specific result.
 * Collapsing the two would pull unevidenced outcome claims into a capabilities
 * pitch — which is exactly how the retired template copy went wrong.
 */

/**
 * Content-layer image key → the bundled asset URL.
 *
 * The indirection exists so `services.json` never carries an import path: the
 * content layer names an image, the component resolves it. `ServiceImageKey`
 * makes the record exhaustive, so an unmapped key fails `tsc` rather than
 * rendering a blank tile.
 */
const SERVICE_IMAGE: Record<ServiceImageKey, string> = {
	AiVisionsComputer,
	BusinessAdvisorLogo,
	CloudManagementLogo,
	ProjectManagementLogo,
	StrategicPlanningLogo,
	SystemDesign,
	TechnologyConsultantLogo
};

const ServiceCard = ({ service, index }: { service: Service; index: number }): JSX.Element => {
	const reduceMotion = useReducedMotion();

	return (
		<motion.li
			className="group h-full"
			initial={reduceMotion ? false : { opacity: 0, y: 28 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, amount: 0.2 }}
			transition={{ duration: 0.55, delay: Math.min(index * 0.06, 0.24), ease: [0.22, 1, 0.36, 1] }}
		>
			{/* `card-shell` / `card-face` are the site-wide card (D-061, index.css).
			    This section is where the treatment was invented (D-031); it now
			    reads it from the one definition rather than carrying its own copy,
			    which is the whole point — the copy in Education had already drifted
			    by an `h-full`. The `group`/`group-hover` pair goes with it: the
			    shell hovers itself now, so the reveal wrapper no longer has to
			    participate in the card's hover state. */}
			<div className="card-shell">
				<article className="card-face flex h-full flex-col p-6">
					{/* THE ACCENT IS THE BRAND RAMP, NOT A CATEGORY HUE, and that is the
					    site's own rule rather than a preference. `index.css` states it
					    where `.pill-tint` is defined: on a category chip the hue is
					    INFORMATION — it is what separates Healthcare from GovTech at a
					    glance — and where a label carries none, the call site takes the
					    shared indigo. Rotating `--cat-*` hues across seven services would
					    imply a taxonomy the content layer does not carry and would drain
					    the tinted pill of the meaning it has everywhere else. So the
					    per-card accent is a hairline of `--grad-brand-*`, the ramp the
					    section headings already use, and the tile keeps the shared
					    indigo tint. */}
					<span
						aria-hidden="true"
						className="block h-[3px] w-12 rounded-full bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)] transition-[width] duration-300 group-hover:w-20"
					/>

					{/* Icon tile. `alt=""` + aria-hidden because the heading beneath already
					    names the service — a described icon would read it out twice. */}
					<span className="mt-5 inline-flex h-16 w-16 items-center justify-center rounded-xl border border-[var(--cat-indigo-border)] bg-[var(--cat-indigo-fill)] shadow-[shadow:var(--glow-tile-rest)] transition-shadow duration-300 group-hover:shadow-[shadow:var(--glow-tile-hover)]">
						{/* 44px inside a 64px tile. These are detailed 500x500 illustrations
						    rather than glyphs, and at 36px they read as indistinct blobs —
						    measured on screenshots at all three widths before settling here. */}
						<img
							src={SERVICE_IMAGE[service.image]}
							alt=""
							aria-hidden="true"
							loading="lazy"
							className="h-11 w-11 object-contain"
						/>
					</span>

					<h3 className="mt-5 text-lg font-bold leading-snug text-[var(--text-strong)]">{service.title}</h3>

					<p className="mt-3 text-sm leading-relaxed text-[var(--text-secondary)]">{service.description}</p>

					{/* No entry carries tags today; the schema allows them, so the rail
					    renders if any ever do. Pushed to the foot so cards in a row align. */}
					{service.tags && service.tags.length > 0 && (
						<div className="mt-auto flex flex-wrap gap-2 pt-5">
							{service.tags.map((tag: string) => (
								<span key={tag} className="chip-quiet">
									{tag}
								</span>
							))}
						</div>
					)}
				</article>
			</div>
		</motion.li>
	);
};

const Services = (): JSX.Element => (
	<section className="section-shell" id="services" aria-labelledby="services-heading">
		<div className="section-rail">
			{/* The heading ramp is `--grad-brand-*`, which is what the other six
			    section headings use. This one carried `--grad-text-*` (the cyan
			    ramp) alone, so the site had two heading gradients for one job.
			    Both clear AA as large text; only one of them is the system. */}
			{/* Owner ruling 2026-08-19 (D-063): every section carries an eyebrow.
			    Five were added in the noun-phrase register, matching
			    Certifications' existing "Verified Credentials". These are
			    structural labels rather than claims about the author, which is
			    why they live in the component like the two that already did —
			    II.2 forbids hardcoding a career FACT, and a section label is
			    not one. */}
			<p className="section-eyebrow">Engagement Models</p>

			<h2 id="services-heading" className="heading">
				<span className="bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)] bg-clip-text text-transparent">
					Services
				</span>{" "}
				Offered
			</h2>

			{/* `auto-rows-fr` and `grid-balanced` are the two halves of one fix
			    (D-099), and neither works without the other.

			    EVERY ROW SIZED ITSELF AND NOTHING SIZED THEM AGAINST EACH OTHER.
			    Measured at 1280 before the change: row one 272px, row two 222px,
			    row three 222px — a 50px step in the middle of a section whose
			    cards are meant to read as one set. A grid row stretches its items
			    to the tallest of THAT row (D-080), which is why the cards within a
			    row already matched and the rows did not. `auto-rows-fr` makes every
			    row an equal fraction, so the tallest description governs all seven.

			    AND THE SEVENTH CARD SAT ALONE against the left edge with two empty
			    columns beside it. `grid-balanced` centres a last card that is the
			    first of its row; the rule and the reason are in index.css. */}
			<ul className="grid-balanced mt-12 grid w-full auto-rows-fr grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 xl:grid-cols-3 xl:gap-8">
				{services.map((service: Service, index: number) => (
					<ServiceCard key={service.id} service={service} index={index} />
				))}
			</ul>
		</div>
	</section>
);

export default Services;
